import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { Spin } from "antd";

import css from "./SelectAvailableDishesModal.module.css";
import { dishesActions } from "../../../../store/slices/dishes-slices";
import { SearchInputComponentModal } from "../SearchInputComponentModal/SearchInputComponentModal";
import SelectSingleComponentModal from "../SelectSingleComponentModal/SelectSingleComponentModal";
import { availableDishesActions } from "../../../../store/slices/menu-slices";
import { availableDishesService } from "../../../../services/menu-services/availableDishes.service";

const SelectAvailableDishesModal = ({ setIsOpen }) => {
  const dispatch = useDispatch();
  const methods = useForm();
  const { setValue, reset, control } = methods;

  const { dishesArray, trigger, dishesFiltered, searchActive, status: dishesStatus } = useSelector(
    (store) => store.dishes
  );
  const { availableDishesArray, dataOfDay } = useSelector(
    (store) => store.availableDishes
  );

  const [selectedDishes, setSelectedDishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const isLoadingDishes = dishesStatus === "loading" || dishesStatus === null;

  // Clear search when component mounts and unmounts
  useEffect(() => {
    dispatch(dishesActions.clearSearch());

    return () => {
      dispatch(dishesActions.clearSearch());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(dishesActions.getAllDishes());
  }, [trigger, dispatch]);

  const dishesToRender = searchActive ? dishesFiltered : dishesArray;

  useEffect(() => {
    const defaultValues = {};
    const initialSelectedDishes = [];

    dishesArray.forEach((d) => {
      const isSelected = availableDishesArray.some(
        (item) => item.dish?.id === d.id
      );
      defaultValues[d.id] = isSelected;
      initialSelectedDishes.push({
        id: d.id,
        checked: isSelected,
      });
    });

    reset(defaultValues);
    setSelectedDishes(initialSelectedDishes);
  }, [dishesArray, availableDishesArray, reset]);

  const watchedValues = useWatch({ control });
  const allSelected = dishesToRender.every((d) => watchedValues?.[d.id]);

  // Update selectedDishes when form values change
  useEffect(() => {
    if (watchedValues && selectedDishes.length > 0) {
      setSelectedDishes((prevSelected) =>
        prevSelected.map((dish) => ({
          ...dish,
          checked: watchedValues[dish.id] || false,
        }))
      );
    }
  }, [watchedValues]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    dishesToRender.forEach((d) => setValue(d.id, checked));

    // Update only the currently visible dishes' checked status
    const visibleDishIds = dishesToRender.map((d) => d.id);
    setSelectedDishes((prevSelected) =>
      prevSelected.map((dish) => ({
        ...dish,
        checked: visibleDishIds.includes(dish.id) ? checked : dish.checked,
      }))
    );
  };

  const isDishSelected = (dishId) => {
    return availableDishesArray.some((item) => item.dish?.id === dishId);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const selectedDishesData = selectedDishes
        .filter((dish) => dish.checked)
        .map((dish) => ({
          dishId: dish.id,
          date: dataOfDay,
        }));

      if (availableDishesArray && availableDishesArray.length > 0) {
        await availableDishesService.updateByDateOfDay(
          dataOfDay,
          selectedDishesData
        );
      } else {
        await availableDishesService.createASAvailableDishes(
          selectedDishesData
        );
      }

      dispatch(availableDishesActions.changeTrigger());
      toast.success("Dishes updated successfully");
      setIsOpen(false);
      reset();
      setSelectedDishes([]);
    } catch (error) {
      console.error("Error updating dishes", error);
      toast.error("Error updating dishes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={css.overlay}>
      <div className={css.modal}>
        <div className={css.header}>
          <h2>Select available dishes</h2>
          <button className={css.closeButton} onClick={() => setIsOpen(false)}>
            &times;
          </button>
        </div>

        <div className={css.blockInput}>
          <SearchInputComponentModal />
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className={css.form}
          >
            <div className={css.table}>
              <div className={css.tableHeader}>
                <div className={css.tableHeaderInput}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    aria-label="Select all dishes"
                  />
                </div>
                <div className={css.tableHeaderImage}>Image</div>
                <div className={css.tableHeaderName}>Name</div>
                <div className={css.tableHeaderWeight}>Weight</div>
                <div className={css.tableHeaderWeightNetto}>Weight Netto</div>
                <div className={css.tableHeaderCat}>Category</div>
              </div>

              <div className={css.tableBody}>
                {isLoadingDishes ? (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  dishesToRender.map((obj) => (
                    <SelectSingleComponentModal
                      key={obj.id}
                      dish={obj}
                      initialChecked={isDishSelected(obj.id)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className={css.actions}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>

              <button type="submit" className={css.saveButton} disabled={saving}>
                {saving ? <Spin size="small" /> : "Save"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export { SelectAvailableDishesModal };
